import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Table as DxTable } from '@devexpress/dx-react-grid-material-ui';

import { Button, IconButton, Tooltip } from '@ds';
import { DSCopyIcon, DSDeleteIcon, DSPlusIcon } from '@ds-icons';

import { BasicModal } from '@DesignSystem/modals';
import { Table } from '@DesignSystem/tables';

import {
  useAddServiceAccountKeyMutatation,
  useServiceAccountApiKeys
} from '@/features/account-settings/api';

import alertUtils from '@/util/alertsUtil';
import { snackbarTypes } from '@/constants/alertTypes';

import { hideApiKeyPart } from '@shared/utils/apiKeys';

import styles from './APIKeysModal.module.scss';
import DeleteAPIKeyModal from './DeleteAPIKeyModal';
import cx from 'classnames';

const COLUMN_KEYS = {
  KEY: 'key',
  CREATED: 'created',
  COPY: 'copy',
  DELETE: 'delete'
} as const;

const COLUMN_WIDTHS = [
  { columnName: COLUMN_KEYS.COPY, width: 40 },
  { columnName: COLUMN_KEYS.DELETE, width: 40 }
];

const columns = [
  { name: COLUMN_KEYS.KEY, title: 'Key' },
  { name: COLUMN_KEYS.CREATED, title: 'Created At' },
  { name: COLUMN_KEYS.COPY, title: ' ' },
  { name: COLUMN_KEYS.DELETE, title: ' ' }
];

type APIKeyRow = {
  [COLUMN_KEYS.KEY]: string;
  [COLUMN_KEYS.CREATED]: string;
};

type APIKeysModalProps = {
  organizationId: string;
  serviceAccountName: string;
  onClose: () => void;
};

const APIKeysModal = ({
  onClose,
  organizationId,
  serviceAccountName
}: APIKeysModalProps) => {
  const dispatch = useDispatch();

  const { data: keysList = [], isLoading } = useServiceAccountApiKeys(
    organizationId,
    serviceAccountName
  );

  const { mutate: addKey } = useAddServiceAccountKeyMutatation();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const handleCopyKey = () => {
    dispatch(
      alertUtils.openSnackbarDialog(
        snackbarTypes.SUCCESS_COPIED_TEXT,
        `Successfully copied API key`
      )
    );
  };

  const handleAddKey = () => {
    addKey({ organizationId, serviceAccountName });
  };

  const rows = keysList.map(({ apiKey, createdAt }) => ({
    [COLUMN_KEYS.KEY]: apiKey,
    [COLUMN_KEYS.CREATED]: moment.unix(createdAt).format('MM/DD/YY hh:mm A')
  }));

  const renderTable = () => {
    const dataTypes = [
      {
        cols: [COLUMN_KEYS.KEY],
        cell: ({ row }: { row: APIKeyRow }) => hideApiKeyPart(row.key)
      },
      {
        cols: [COLUMN_KEYS.COPY],
        cell: ({ row }: { row: APIKeyRow }) => (
          <CopyToClipboard text={row.key} onCopy={handleCopyKey}>
            <Tooltip wrapper={false} content="Copy key">
              <IconButton type="secondary" Icon={<DSCopyIcon />} />
            </Tooltip>
          </CopyToClipboard>
        )
      },
      {
        cols: [COLUMN_KEYS.DELETE],
        cell: ({ row }: { row: APIKeyRow }) => (
          <Tooltip wrapper={false} content="Delete key">
            <IconButton
              onClick={() => setSelectedKey(row.key)}
              type="secondary"
              Icon={<DSDeleteIcon />}
            />
          </Tooltip>
        )
      }
    ];

    return (
      <div className={styles.apiKeysModalContent}>
        <Table
          columns={columns}
          className={styles.apiKeysTable}
          rowHeight="60px"
          rowIdKey={COLUMN_KEYS.KEY}
          rows={rows}
          maxHeight={360}
          isFetching={isLoading}
          dataTypes={dataTypes}
          hideLastRowBorder={false}
          paginationConfig={{ isDisabled: true }}
          selectionConfig={{ isDisabled: true }}
          sortingConfig={{ isDisabled: true }}
          columnOrderConfig={{ isDisabled: true }}
          columnWidthsConfig={{
            isDisabled: true,
            columnExtensions: COLUMN_WIDTHS
          }}
          renderEmptyState={props => (
            <DxTable.Cell
              {...props}
              className={cx(styles.emptyStateViewContainer, props.className)}
              colSpan={10}
            >
              <p> No API keys </p>
            </DxTable.Cell>
          )}
        />
        <Button
          onClick={handleAddKey}
          type="secondary"
          size="large"
          PrefixIcon={<DSPlusIcon />}
        >
          Add Key
        </Button>
      </div>
    );
  };

  return (
    <>
      <BasicModal
        open
        onClose={onClose}
        additionalClasses={{ paper: styles.modalPaper }}
        title={`Manage "${serviceAccountName}" API keys`}
        content={renderTable()}
      />

      <DeleteAPIKeyModal
        onClose={() => setSelectedKey(null)}
        isOpen={Boolean(selectedKey)}
        apiKey={selectedKey || ''}
        organizationId={organizationId}
        serviceAccountName={serviceAccountName}
      />
    </>
  );
};

export default APIKeysModal;
